import { useState, useEffect, type ReactNode, type ReactElement } from 'react'
import { Flex } from '@magnetic/flex'
import { Card } from '@magnetic/card'
import { Text } from '@magnetic/text'
import { FilterBar } from '@magnetic/filter-bar'
import { Search } from '@magnetic/search'
import { Spinner } from '@magnetic/spinner'
import { Table, useMagneticTable, getPaginationRowModel, getSelectionColumn, type MdsTableColumn } from '@magnetic/table'
import { useQuery as useConnectQuery } from '@connectrpc/connect-query'
import { Tag } from '@magnetic/tag'
import { Select } from '@magnetic/select'
import { User, Users } from '@/lib/icons'
import type { ProjectMember as ProtoProjectMember } from '@/lib/generated-types'
import type { ListProjectMembersResponse } from '@/gen/catpocs/v1/projects_pb'
import type { listProjectMembers } from '@/lib/api'

export type ProjectMember = ProtoProjectMember

interface MemberRow extends ProjectMember {
  id: string
}

interface MembersTableProps {
  // Project ID to fetch members for
  projectId: string
  // RPC query function to use
  queryFn: typeof listProjectMembers
  // Optional title
  title?: string
  // Optional description
  description?: string
  // Enable row selection
  selectableRows?: boolean
  // Custom configuration components (buttons) for the FilterBar
  configComponents?: ReactElement | null
  // Custom bulk actions for selected rows - receives selected count and selected rows
  bulkActions?: (selectedCount: number, selectedRows: MemberRow[]) => ReactNode
  // Initial page size
  initialPageSize?: number
  // Custom action column
  renderActions?: (member: MemberRow) => ReactNode
}

export function MembersTable({
  projectId,
  queryFn,
  title,
  description,
  selectableRows = false,
  configComponents,
  bulkActions,
  initialPageSize = 20,
  renderActions,
}: MembersTableProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [showReset, setShowReset] = useState(false)

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery)
      setCurrentPage(1) // Reset to first page on search
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // Show/hide reset button
  useEffect(() => {
    setShowReset(debouncedSearch !== '' || roleFilter !== '')
  }, [debouncedSearch, roleFilter])

  // Calculate pagination parameters
  const pageSize = initialPageSize
  const skip = (currentPage - 1) * pageSize

  // Fetch data with pagination and search
  const { data: rawData, isLoading, error } = useConnectQuery(queryFn, {
    projectId,
    pageSize: pageSize,
    skip: skip,
    search: debouncedSearch,
    roleFilter: roleFilter,
  })

  const response = rawData as ListProjectMembersResponse | undefined
  const members: ProjectMember[] = response?.members ?? []
  const totalCount = response?.totalCount ?? 0

  // Transform members for table (add unique id)
  const membersData: MemberRow[] = members.map((member, index) => ({
    ...member,
    id: member.userId || member.groupId || `member-${index}`,
  }))

  const memberColumns: MdsTableColumn<MemberRow>[] = [
    {
      id: 'type',
      header: 'Type',
      accessorFn: (row) => (row.userId ? 'User' : 'Group'),
      cell: ({ getValue, row }) => (
        <Flex direction="horizontal" gap={8} align="center">
          {row.original.userId ? <User size={16} /> : <Users size={16} />}
          <Text size="p3">{getValue() as string}</Text>
        </Flex>
      ),
    },
    {
      id: 'identifier',
      header: 'Identifier',
      accessorFn: (row) => (row.userId ? row.email || row.userId : row.groupId),
      cell: ({ getValue }) => <Text>{getValue() as string}</Text>,
    },
    {
      id: 'role',
      header: 'Role',
      accessorKey: 'role',
      cell: ({ getValue }) => <Tag>{getValue() as string}</Tag>,
    },
  ]

  // Add action column if renderActions is provided
  if (renderActions) {
    memberColumns.push({
      id: 'actions',
      header: '',
      cell: ({ row }) => renderActions(row.original),
    })
  }

  // Add selection column if enabled
  const tableColumns: MdsTableColumn<MemberRow>[] = selectableRows
    ? [getSelectionColumn<MemberRow>(), ...memberColumns]
    : memberColumns

  const { table } = useMagneticTable({
    data: membersData,
    columns: tableColumns,
    enableRowSelection: selectableRows,
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    pageCount: Math.ceil(totalCount / pageSize),
    state: {
      pagination: {
        pageIndex: currentPage - 1,
        pageSize: pageSize,
      },
    },
  })

  const selectedRows = selectableRows
    ? table.getSelectedRowModel().rows.map((row) => row.original)
    : []
  const selectedCount = selectedRows.length

  const handleReset = () => {
    setSearchQuery('')
    setDebouncedSearch('')
    setRoleFilter('')
    setCurrentPage(1)
  }

  return (
    <Flex direction="vertical" gap={16}>
      {(title || description) && (
        <Flex direction="vertical" gap={8}>
          {title && <Text size="h3">{title}</Text>}
          {description && <Text size="p2">{description}</Text>}
        </Flex>
      )}

      <Card>
        <Flex direction="vertical" gap={16}>
          <FilterBar
            searchBar={
              <Search
                fixedWidth
                label="Search members"
                placeholder="Search by email, user ID, or group ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClear={searchQuery ? () => setSearchQuery('') : undefined}
                size="md"
              />
            }
            results={totalCount}
            isLoadingResults={isLoading}
            showResetButton={showReset}
            onReset={handleReset}
            configComponents={configComponents ?? undefined}
          >
            <Flex style={{ minWidth: '150px' }}>
              <Select
                placeholder="Filter by role"
                value={roleFilter}
                onChange={(value) => setRoleFilter(value as string)}
                options={[
                  { value: '', label: 'All Roles' },
                  { value: 'admin', label: 'Admin' },
                  { value: 'editor', label: 'Editor' },
                  { value: 'viewer', label: 'Viewer' },
                ]}
              />
            </Flex>
          </FilterBar>

          {isLoading && (
            <Flex align="center" justify="center" style={{ padding: '32px' }}>
              <Spinner size="lg" />
            </Flex>
          )}

          {error && (
            <Flex direction="vertical" align="center" gap={16} style={{ padding: '32px' }}>
              <Text>Failed to load members</Text>
              <Text size="sm" color="light">
                {error instanceof Error ? error.message : 'Unknown error'}
              </Text>
            </Flex>
          )}

          {!isLoading && !error && membersData.length === 0 && (
            <Flex direction="vertical" align="center" gap={8} style={{ padding: '32px', opacity: 0.5 }}>
              <User size={32} />
              <Text>No members found</Text>
            </Flex>
          )}

          {!isLoading && !error && membersData.length > 0 && (
            <Table
              table={table}
              selectableRows={selectableRows}
              paginationControls={totalCount > pageSize ? {} : undefined}
            >
              {selectableRows && bulkActions && (
                <Table.BulkActionBar>
                  <Flex direction="horizontal" gap={8} align="center">
                    {bulkActions(selectedCount, selectedRows)}
                  </Flex>
                </Table.BulkActionBar>
              )}
            </Table>
          )}
        </Flex>
      </Card>
    </Flex>
  )
}
